import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    IconButton,
    InputAdornment,
    Container,
} from "@mui/material";
import {
    Visibility,
    VisibilityOff,
} from "@mui/icons-material";
import { Alert, CircularProgress } from "@mui/material";
import authService from "../services/authService";
import CareXLogo from "../components/CareXLogo";

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    const [username, setUsername] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username.trim() || !newPassword.trim() || !confirmPassword.trim()) {
            setMessage({ text: "Please fill in all fields (Username, New Password, and Confirm Password).", type: "error" });
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage({ text: "Passwords do not match.", type: "error" });
            return;
        }

        if (!token) {
            setMessage({ text: "Invalid or missing reset token.", type: "error" });
            return;
        }

        setLoading(true);
        setMessage(null);
        try {
            await authService.resetPasswordWithToken(token, username, newPassword, confirmPassword);

            setMessage({ text: "Password reset successfully", type: "success" });
            setUsername("");
            setNewPassword("");
            setConfirmPassword("");

            setTimeout(() => navigate("/login"), 3000);
        } catch (err: any) {
            setMessage({ text: err.message || "Failed to reset password.", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(145deg, #EBF5FF 0%, #F0FAFE 40%, #E6F7FF 70%, #EEF9F0 100%)",
                p: 2,
            }}
        >
            <Box sx={{ mt: 4, mb: 4 }}>
                <CareXLogo style={{ height: "auto", width: "220px", maxWidth: "90vw" }} />
            </Box>

            <Container sx={{ maxWidth: "450px !important" }}>
                <Card
                    sx={{
                        borderRadius: "32px",
                        boxShadow: "0 35px 70px -15px rgba(0, 0, 0, 0.6)",
                        background: "#F8FAFB",
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                mb: 3,
                                textAlign: "center",
                                color: "#0077B6",
                            }}
                        >
                            Reset Password
                        </Typography>

                        <Box
                            component="form"
                            onSubmit={handleReset}
                            sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                        >
                            {message && (
                                <Alert
                                    severity={message.type}
                                    sx={{
                                        borderRadius: "16px",
                                        mb: 2,
                                        "& .MuiAlert-message": {
                                            fontSize: "1rem",
                                            color: message.type === "success" ? "#0ea5e9" : "inherit",
                                            fontWeight: message.type === "success" ? "bold" : "normal"
                                        }
                                    }}
                                >
                                    {message.text}
                                </Alert>
                            )}

                            <TextField
                                fullWidth
                                placeholder="Username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                autoComplete="off"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "12px",
                                        backgroundColor: "white",
                                        fontSize: "1rem",
                                        height: "54px",
                                    },
                                }}
                            />

                            <TextField
                                fullWidth
                                placeholder="New Password"
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                edge="end"
                                                sx={{ color: "#637381" }}
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                autoComplete="new-password"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "12px",
                                        backgroundColor: "white",
                                        fontSize: "1rem",
                                        height: "54px",
                                    },
                                }}
                            />

                            <TextField
                                fullWidth
                                placeholder="Confirm Password"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() =>
                                                    setShowConfirmPassword(!showConfirmPassword)
                                                }
                                                edge="end"
                                                sx={{ color: "#637381" }}
                                            >
                                                {showConfirmPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                autoComplete="new-password"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "12px",
                                        backgroundColor: "white",
                                        fontSize: "1rem",
                                        height: "54px",
                                    },
                                }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{
                                    mt: 2,
                                    py: 1.5,
                                    fontWeight: 600,
                                    fontSize: "1.1rem",
                                    borderRadius: "12px",
                                    background: "#0077B6",
                                    textTransform: "none",
                                    boxShadow: "0 4px 12px rgba(0, 119, 182, 0.35)",
                                    "&:hover": {
                                        background: "#005F8A",
                                        opacity: 0.95,
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 6px 16px rgba(0, 119, 182, 0.45)",
                                    },
                                    transition: "all 0.2s ease",
                                }}
                            >
                                {loading ? <CircularProgress size={28} color="inherit" /> : "Reset Password"}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default ResetPasswordPage;